import React from 'react'
import { useMutation, useQuery } from '@apollo/client'
import type { ResultOf } from 'gql.tada'
import { format, parse } from 'date-fns'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { TimePicker } from '@/components'
import { Select } from '@/components_deprecated'
import {
  COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY,
  PATCH_COMPANY_WORK_INFRINGEMENT_SETTINGS,
} from '@/modules/work-infringements/fats-settings-operations'
import type { FormButton } from '../components/common'
import {
  Field,
  Form,
  InfoBox,
  OldSection,
  SettingsWrapper,
} from '../components/common'

type CompanySettings = NonNullable<
  ResultOf<
    typeof COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY
  >['companyWorkInfringementSettings']
>

interface FATSParametersForm {
  referencePeriodWeeks: number
  maxDailyWorkHours: number
  nightRuleEnabled: boolean
  nightDailyLimitHours: number
  nightWindowStart: Date
  nightWindowEnd: Date
  maxWeeklyWorkHours: number
  maxAverageWeeklyWorkHours: number
  weeklyOrdinaryHours: number
  annualOvertimeLimitHours: number
}

// Backend Time scalar is serialized as HH:MM:SS; TimePicker works on Date.
const toPickerTime = (value: string) => parse(value, 'HH:mm:ss', new Date())
const toApiTime = (value: Date) => format(value, 'HH:mm:ss')

const rangeOptions = (
  from: number,
  count: number,
  label: (n: number) => string
) =>
  Array.from({ length: count }, (_, i) => ({
    label: label(from + i),
    value: from + i,
  }))

export const FATSParametersSettings: React.FC = () => {
  const { data, loading } = useQuery(COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY)
  const settings = data?.companyWorkInfringementSettings
  if (loading || !settings) return null
  return <FATSParametersForm settings={settings} />
}

const FATSParametersForm: React.FC<{ settings: CompanySettings }> = ({
  settings,
}) => {
  const t = useTranslate({
    title: 'settings.fats-parameters.title',
    info: 'settings.fats-parameters.info',
    periodLength: 'settings.fats.period-length',
    maxDailyWorkLimit: 'settings.fats.max-daily-work-limit',
    nightRule: 'settings.fats-parameters.night-rule',
    nightDailyLimit: 'settings.fats-parameters.night-daily-limit',
    nightWindowStart: 'settings.fats-parameters.night-window-start',
    nightWindowEnd: 'settings.fats-parameters.night-window-end',
    maxWeekly: 'settings.fats-parameters.max-weekly',
    avgWeekly: 'settings.fats-parameters.avg-weekly',
    weeklyOrdinary: 'settings.fats-parameters.weekly-ordinary',
    annualOvertime: 'settings.fats-parameters.annual-overtime',
    nWeeks: ['settings.fats.n-weeks', { n: 0 }],
    nHours: ['common.n-hours', { n: 0 }],
    enabled: 'common.enabled',
    disabled: 'common.disabled',
    save: 'common.save',
  })

  const [patchSettings, { loading: saving }] = useMutation(
    PATCH_COMPANY_WORK_INFRINGEMENT_SETTINGS
  )

  const {
    formValues,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<FATSParametersForm>({
    values: {
      referencePeriodWeeks: settings.referencePeriodWeeks,
      maxDailyWorkHours: settings.maxDailyWorkHours,
      nightRuleEnabled: settings.nightRuleEnabled,
      nightDailyLimitHours: settings.nightDailyLimitHours,
      nightWindowStart: toPickerTime(settings.nightWindowStart),
      nightWindowEnd: toPickerTime(settings.nightWindowEnd),
      maxWeeklyWorkHours: settings.maxWeeklyWorkHours,
      maxAverageWeeklyWorkHours: settings.maxAverageWeeklyWorkHours,
      weeklyOrdinaryHours: settings.weeklyOrdinaryHours,
      annualOvertimeLimitHours: settings.annualOvertimeLimitHours,
    },
  })

  const periodLengthOptions = rangeOptions(16, 11, n => t.nWeeks({ n }))
  const maxDailyOptions = rangeOptions(13, 4, n => t.nHours({ n }))
  const nightDailyOptions = rangeOptions(8, 6, n => t.nHours({ n }))
  const maxWeeklyOptions = rangeOptions(48, 19, n => t.nHours({ n }))
  const avgWeeklyOptions = rangeOptions(40, 15, n => t.nHours({ n }))
  const weeklyOrdinaryOptions = rangeOptions(35, 14, n => t.nHours({ n }))
  const annualOvertimeOptions = [200, 250, 300, 350, 400].map(n => ({
    label: t.nHours({ n }),
    value: n,
  }))
  const nightRuleOptions = [
    { label: t.enabled, value: 'true' },
    { label: t.disabled, value: 'false' },
  ]

  async function handleSave(values: FATSParametersForm) {
    await patchSettings({
      variables: {
        referencePeriodWeeks: values.referencePeriodWeeks,
        maxDailyWorkHours: values.maxDailyWorkHours,
        nightRuleEnabled: values.nightRuleEnabled,
        nightDailyLimitHours: values.nightDailyLimitHours,
        nightWindowStart: toApiTime(values.nightWindowStart),
        nightWindowEnd: toApiTime(values.nightWindowEnd),
        maxWeeklyWorkHours: values.maxWeeklyWorkHours,
        maxAverageWeeklyWorkHours: values.maxAverageWeeklyWorkHours,
        weeklyOrdinaryHours: values.weeklyOrdinaryHours,
        annualOvertimeLimitHours: values.annualOvertimeLimitHours,
      },
    })
  }

  const formButtons: FormButton[] = [
    {
      text: t.save,
      type: 'submit',
      buttonProps: { disabled: saving },
      onClick: submit(handleSave),
    },
  ]

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox>
        <p>{t.info}</p>
      </InfoBox>
      <OldSection>
        <header>{t.title}</header>
        <Form buttons={formButtons}>
          <Field>
            <label>{t.periodLength}</label>
            <Select
              options={periodLengthOptions}
              value={formValues.referencePeriodWeeks}
              onChange={v =>
                handler('referencePeriodWeeks')(
                  v ?? formValues.referencePeriodWeeks
                )
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.maxDailyWorkLimit}</label>
            <Select
              options={maxDailyOptions}
              value={formValues.maxDailyWorkHours}
              onChange={v =>
                handler('maxDailyWorkHours')(v ?? formValues.maxDailyWorkHours)
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.maxWeekly}</label>
            <Select
              options={maxWeeklyOptions}
              value={formValues.maxWeeklyWorkHours}
              onChange={v =>
                handler('maxWeeklyWorkHours')(
                  v ?? formValues.maxWeeklyWorkHours
                )
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.avgWeekly}</label>
            <Select
              options={avgWeeklyOptions}
              value={formValues.maxAverageWeeklyWorkHours}
              onChange={v =>
                handler('maxAverageWeeklyWorkHours')(
                  v ?? formValues.maxAverageWeeklyWorkHours
                )
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.weeklyOrdinary}</label>
            <Select
              options={weeklyOrdinaryOptions}
              value={formValues.weeklyOrdinaryHours}
              onChange={v =>
                handler('weeklyOrdinaryHours')(
                  v ?? formValues.weeklyOrdinaryHours
                )
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.annualOvertime}</label>
            <Select
              options={annualOvertimeOptions}
              value={formValues.annualOvertimeLimitHours}
              onChange={v =>
                handler('annualOvertimeLimitHours')(
                  v ?? formValues.annualOvertimeLimitHours
                )
              }
              nullable={false}
              width="260px"
            />
          </Field>
          <Field>
            <label>{t.nightRule}</label>
            <Select
              options={nightRuleOptions}
              value={String(formValues.nightRuleEnabled)}
              onChange={v => handler('nightRuleEnabled')(v === 'true')}
              nullable={false}
              width="260px"
            />
          </Field>
          {formValues.nightRuleEnabled && (
            <>
              <Field>
                <label>{t.nightDailyLimit}</label>
                <Select
                  options={nightDailyOptions}
                  value={formValues.nightDailyLimitHours}
                  onChange={v =>
                    handler('nightDailyLimitHours')(
                      v ?? formValues.nightDailyLimitHours
                    )
                  }
                  nullable={false}
                  width="260px"
                />
              </Field>
              <Field>
                <label>{t.nightWindowStart}</label>
                <TimePicker
                  value={formValues.nightWindowStart}
                  onChange={handler('nightWindowStart')}
                />
              </Field>
              <Field>
                <label>{t.nightWindowEnd}</label>
                <TimePicker
                  value={formValues.nightWindowEnd}
                  onChange={handler('nightWindowEnd')}
                />
              </Field>
            </>
          )}
        </Form>
      </OldSection>
    </SettingsWrapper>
  )
}
